import React from 'react';
import { Container, Typography, Paper, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export default function GraphqlBasics1Docs() {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          GraphQL Basics - Part 1
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>
          🏠 HOME
        </Button>
      </Box>

      <Typography variant="body1" sx={{ mb: 3 }}>
        GraphQL의 기본 개념과 스키마 정의 방법을 학습합니다. 
        REST API와의 차이점을 이해하고, 효율적인 데이터 요청 방법을 익혀보겠습니다.
      </Typography>

      <Typography variant="h4" gutterBottom sx={{ mt: 4, mb: 2 }}>
        기본 개념과 스키마
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        GraphQL이란?
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Facebook에서 개발한 쿼리 언어로, 클라이언트가 필요한 데이터만 정확히 요청할 수 있게 해줍니다. 
        단일 엔드포인트를 통해 복잡한 데이터 관계도 한 번에 가져올 수 있습니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`// REST API의 문제점
GET /api/users/1           // 사용자 정보
GET /api/users/1/posts     // 사용자의 게시물
GET /api/posts/1/comments  // 게시물의 댓글

// 3번의 요청이 필요하고, 필요 없는 데이터도 함께 받음

// GraphQL 방식
query {
  user(id: 1) {
    name
    email
    posts {
      title
      comments {
        content
        author
      }
    }
  }
}

// 한 번의 요청으로 필요한 데이터만 정확히 받음`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        GraphQL 스키마 정의
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        스키마는 API의 구조를 정의하는 계약서입니다. 어떤 데이터를 요청할 수 있는지, 
        어떤 형태로 응답이 오는지를 명확하게 정의합니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`# 기본 타입 정의
type User {
  id: ID!              # 필수 필드 (!)
  name: String!
  email: String!
  age: Int
  posts: [Post!]!      # Post 배열
}

type Post {
  id: ID!
  title: String!
  content: String!
  author: User!        # User 객체 참조
  createdAt: String!
  comments: [Comment!]!
}

type Comment {
  id: ID!
  content: String!
  author: User!
  post: Post!
  createdAt: String!
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Query 정의 (데이터 조회)
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Query는 데이터를 읽기 위한 작업을 정의합니다. REST의 GET 요청과 비슷하지만, 
        반환할 필드를 클라이언트가 직접 선택할 수 있습니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`type Query {
  # 모든 사용자 조회
  users: [User!]!
  
  # 특정 사용자 조회
  user(id: ID!): User
  
  # 게시물 조회 (페이지네이션 포함)
  posts(
    first: Int
    after: String
    filter: PostFilter
  ): PostConnection!
  
  # 검색 기능
  search(query: String!): [SearchResult!]!
}

# 입력 타입 정의
input PostFilter {
  authorId: ID
  category: String
  published: Boolean
}

# 연결 타입 (페이지네이션용)
type PostConnection {
  edges: [PostEdge!]!
  pageInfo: PageInfo!
  totalCount: Int!
}

type PostEdge {
  node: Post!
  cursor: String!
}

type PageInfo {
  hasNextPage: Boolean!
  hasPreviousPage: Boolean!
  startCursor: String
  endCursor: String
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Mutation 정의 (데이터 변경)
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Mutation은 데이터를 생성, 수정, 삭제하기 위한 작업입니다. 
        REST의 POST, PUT, DELETE와 비슷하지만 더 명시적이고 타입 안전합니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`type Mutation {
  # 사용자 생성
  createUser(input: CreateUserInput!): User!
  
  # 사용자 수정
  updateUser(id: ID!, input: UpdateUserInput!): User!
  
  # 사용자 삭제
  deleteUser(id: ID!): Boolean!
  
  # 게시물 작성
  createPost(input: CreatePostInput!): Post!
  
  # 게시물 수정
  updatePost(id: ID!, input: UpdatePostInput!): Post!
  
  # 댓글 작성
  addComment(postId: ID!, content: String!): Comment!
}

# 입력 타입들
input CreateUserInput {
  name: String!
  email: String!
  age: Int
}

input UpdateUserInput {
  name: String
  email: String
  age: Int
}

input CreatePostInput {
  title: String!
  content: String!
  authorId: ID!
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Subscription 정의 (실시간 데이터)
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Subscription은 실시간으로 데이터 변경사항을 받을 수 있게 해줍니다. 
        WebSocket을 사용하여 서버에서 클라이언트로 실시간 알림을 보냅니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`type Subscription {
  # 새 게시물 알림
  postAdded: Post!
  
  # 특정 게시물의 새 댓글 알림
  commentAdded(postId: ID!): Comment!
  
  # 사용자 온라인 상태 변경
  userStatusChanged: UserStatus!
  
  # 채팅 메시지
  messageAdded(chatId: ID!): Message!
}

type UserStatus {
  userId: ID!
  isOnline: Boolean!
  lastSeen: String
}

type Message {
  id: ID!
  content: String!
  author: User!
  chatId: ID!
  createdAt: String!
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        스칼라 타입과 열거형
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        GraphQL의 기본 타입들과 커스텀 열거형을 정의하는 방법입니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`# 기본 스칼라 타입
# String, Int, Float, Boolean, ID

# 커스텀 스칼라 타입
scalar DateTime
scalar Email
scalar URL

# 열거형 정의
enum UserRole {
  ADMIN
  MODERATOR
  USER
  GUEST
}

enum PostStatus {
  DRAFT
  PUBLISHED
  ARCHIVED
}

# 사용 예시
type User {
  id: ID!
  name: String!
  email: Email!           # 커스텀 스칼라
  role: UserRole!         # 열거형
  createdAt: DateTime!    # 커스텀 스칼라
  website: URL           # 선택적 필드
}

type Post {
  id: ID!
  title: String!
  status: PostStatus!
  publishedAt: DateTime
}`}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/docs/graphql-basics')}
        >
          ← GraphQL 개요
        </Button>
        <Button 
          variant="contained" 
          onClick={() => navigate('/docs/graphql-basics-2')}
        >
          PART 2: Apollo Client 설정 →
        </Button>
      </Box>
    </Container>
  );
}
